import { useState, useEffect } from 'react';
import { db, storage } from '../../firebase/config';
import { collection, addDoc, doc, getDoc, updateDoc, getDocs, query, orderBy, limit, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const EventForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [status, setStatus] = useState(''); // '' means not loading
    const [fetching, setFetching] = useState(isEditMode);

    const [formData, setFormData] = useState({
        title: '',
        date: '',
        content: '',
        image: '' // This will store the URL
    });

    // File inputs
    const [imageFile, setImageFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [galleryFiles, setGalleryFiles] = useState([]);
    const [existingGallery, setExistingGallery] = useState([]);

    useEffect(() => {
        if (isEditMode) {
            const fetchEvent = async () => {
                const docRef = doc(db, "events", id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setFormData(data);
                    setPreviewUrl(data.image);
                    if (data.gallery) setExistingGallery(data.gallery);
                } else {
                    alert("Etkinlik bulunamadı!");
                    navigate('/admin/events');
                }
                setFetching(false);
            };
            fetchEvent();
        }
    }, [id, isEditMode, navigate]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleGalleryChange = (e) => {
        const files = Array.from(e.target.files);
        setGalleryFiles(prev => [...prev, ...files]);
    };

    const removeGalleryFile = (index) => {
        setGalleryFiles(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingGalleryImage = (urlToRemove) => {
        setExistingGallery(prev => prev.filter(url => url !== urlToRemove));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('Görseller hazırlanıyor...');

        try {
            let imageUrl = formData.image;

            // Upload Main Image
            if (imageFile) {
                setStatus('Kapak resmi yükleniyor...');
                const storageRef = ref(storage, `events/${Date.now()}_${imageFile.name}`);
                const snapshot = await uploadBytes(storageRef, imageFile);
                imageUrl = await getDownloadURL(snapshot.ref);
            }

            // Upload Gallery Images
            let newGalleryUrls = [];
            if (galleryFiles.length > 0) {
                setStatus(`Galeri yükleniyor (0/${galleryFiles.length})...`);
                const uploadPromises = galleryFiles.map(async (file, index) => {
                    setStatus(`Galeri yükleniyor (${index + 1}/${galleryFiles.length})...`);
                    const storageRef = ref(storage, `events/gallery/${Date.now()}_${file.name}`);
                    const snapshot = await uploadBytes(storageRef, file);
                    return await getDownloadURL(snapshot.ref);
                });
                newGalleryUrls = await Promise.all(uploadPromises);
            }

            setStatus('Veritabanına kaydediliyor...');
            const currentGallery = [...existingGallery, ...newGalleryUrls];

            const eventData = {
                title: formData.title,
                date: formData.date,
                content: formData.content,
                image: imageUrl,
                gallery: currentGallery,
                updatedAt: new Date().toISOString()
            };

            if (isEditMode) {
                await updateDoc(doc(db, "events", id), eventData);
            } else {
                // Determine new order and update existing events
                const eventsQuery = query(collection(db, "events"));
                const eventsSnap = await getDocs(eventsQuery);

                const batch = writeBatch(db);

                // Increment the order of all existing events by 1
                eventsSnap.forEach((docSnapshot) => {
                    const eventRef = doc(db, "events", docSnapshot.id);
                    const currentOrder = docSnapshot.data().order || 0;
                    batch.update(eventRef, { order: currentOrder + 1 });
                });

                // Add the new event with order = 1
                const newEventRef = doc(collection(db, "events"));
                batch.set(newEventRef, {
                    ...eventData,
                    order: 1,
                    createdAt: new Date().toISOString()
                });

                await batch.commit();
            }

            setStatus('Tamamlandı!');
            setTimeout(() => navigate('/admin/events'), 500);

        } catch (error) {
            console.error("Error saving event:", error);
            alert("HATA: " + error.message);
            setStatus('');
        }
    };

    if (fetching) return <div className="text-white">Veri yükleniyor...</div>;

    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
                <Link to="/admin/events" className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors">
                    <ArrowLeft size={20} />
                </Link>
                <h1 className="text-3xl font-bold text-white">
                    {isEditMode ? 'Etkinliği Düzenle' : 'Yeni Etkinlik Ekle'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="bg-gray-800 rounded-xl border border-gray-700 p-8 space-y-4">

                {/* 1. Main Info */}
                <div className="space-y-6">
                    {/* Title */}
                    <div>
                        <label className="block text-gray-400 text-sm font-medium mb-2">Başlık</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                            placeholder="Örn: Kapadokya Turu"
                            required
                        />
                    </div>

                    {/* Date */}
                    <div>
                        <label className="block text-gray-400 text-sm font-medium mb-2">Tarih</label>
                        <input
                            type="text"
                            value={formData.date}
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                            placeholder="DD.MM.YYYY"
                            required
                        />
                    </div>

                    {/* Content */}
                    <div>
                        <label className="block text-gray-400 text-sm font-medium mb-2">İçerik</label>
                        <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
                            <ReactQuill
                                theme="snow"
                                value={formData.content}
                                onChange={(content) => setFormData({ ...formData, content })}
                                className="text-white h-64"
                                modules={{
                                    toolbar: [
                                        [{ 'header': [1, 2, false] }],
                                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                        ['link'],
                                        ['clean']
                                    ],
                                }}
                            />
                        </div>
                        <style>{`
                            .ql-toolbar {
                                background: #e2e8f0;
                                border-bottom: 1px solid #475569 !important;
                            }
                            .ql-container {
                                font-size: 1rem;
                            }
                            .ql-editor {
                                min-height: 16rem;
                                color: #f8fafc; /* Text color inside editor */
                            }
                            .ql-editor.ql-blank::before {
                                color: #94a3b8; /* Placeholder color */
                            }
                        `}</style>
                    </div>
                </div>

                {/* 2. Main Image */}
                <div className="space-y-4 pt-6 border-t border-gray-700">
                    <h3 className="text-xl font-bold text-white">Kapak Görseli</h3>

                    {/* Method Selection: URL or Upload */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-gray-400 text-sm font-medium mb-2">Görsel URL (İnternetten kopyala yapıştır)</label>
                            <input
                                type="text"
                                value={formData.image}
                                onChange={(e) => {
                                    setFormData({ ...formData, image: e.target.value });
                                    setPreviewUrl(e.target.value);
                                }}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                                placeholder="https://..."
                            />
                            <p className="text-xs text-gray-500 mt-1">Eğer dosya yüklemek istemiyorsanız buraya bir resim linki yapıştırabilirsiniz.</p>
                        </div>

                        <div className="flex items-center gap-4">
                            <span className="text-gray-500 text-sm">VEYA Dosya Yükle:</span>
                        </div>

                        <div className="flex flex-col md:flex-row items-start gap-6">
                            <div className="w-full md:w-64 h-40 bg-gray-900 rounded-lg border-2 border-dashed border-gray-700 flex items-center justify-center overflow-hidden relative group">
                                {previewUrl ? (
                                    <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                                ) : (
                                    <div className="text-gray-500 flex flex-col items-center">
                                        <Upload size={24} className="mb-2" />
                                        <span className="text-xs">Bilgisayardan Seç</span>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    onChange={handleImageChange}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                    accept="image/*"
                                />
                            </div>
                            <div className="flex-1 text-sm text-gray-500">
                                <p>Dosya seçerseniz, URL yerine dosya yüklenir.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. Gallery */}
                <div className="space-y-4 pt-6 border-t border-gray-700">
                    <div className="flex justify-between items-center">
                        <h3 className="text-xl font-bold text-white">Galeri</h3>
                        <label className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg cursor-pointer text-sm font-medium transition-colors flex items-center gap-2">
                            <Upload size={16} />
                            Fotoğraf Ekle
                            <input type="file" multiple onChange={handleGalleryChange} className="hidden" accept="image/*" />
                        </label>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {/* Existing Images */}
                        {existingGallery.map((url, idx) => (
                            <div key={`exist-${idx}`} className="relative group aspect-square bg-black rounded-lg overflow-hidden border border-gray-700">
                                <img src={url} alt={`Gallery ${idx}`} className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => removeExistingGalleryImage(url)}
                                    className="absolute top-2 right-2 p-1 bg-red-500/80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <ArrowLeft size={16} className="rotate-45" /> {/* Close icon lookalike */}
                                </button>
                                <span className="absolute bottom-2 left-2 text-xs bg-black/50 px-2 py-1 rounded text-white">Kayıtlı</span>
                            </div>
                        ))}

                        {/* New Files to Upload */}
                        {galleryFiles.map((file, idx) => (
                            <div key={`new-${idx}`} className="relative group aspect-square bg-gray-900 rounded-lg overflow-hidden border border-cyan-500/50">
                                <img src={URL.createObjectURL(file)} alt="Preview" className="w-full h-full object-cover opacity-80" />
                                <button
                                    type="button"
                                    onClick={() => removeGalleryFile(idx)}
                                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                                >
                                    <ArrowLeft size={16} className="rotate-45" />
                                </button>
                                <span className="absolute bottom-2 left-2 text-xs bg-cyan-600 px-2 py-1 rounded text-white">Yeni</span>
                            </div>
                        ))}
                    </div>
                    {existingGallery.length === 0 && galleryFiles.length === 0 && (
                        <div className="text-center py-8 text-gray-500 bg-gray-900/50 rounded-lg border border-gray-700 border-dashed">
                            Henüz galeriye fotoğraf eklenmemiş.
                        </div>
                    )}
                </div>

                {/* Submit */}
                <div className="pt-6 border-t border-gray-700 flex justify-end">
                    <button
                        type="submit"
                        disabled={!!status}
                        className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-3 rounded-lg font-bold flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {status ? (
                            <>
                                <span className="animate-spin">⏳</span>
                                <span>{status}</span>
                            </>
                        ) : (
                            <>
                                <Save size={20} />
                                <span>Kaydet</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EventForm;
